import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatMenuModule } from '@angular/material/menu';
import { InfoHintComponent } from '../../components/shared/info-hint.component';
import { PersonLinkComponent } from '../../components/shared/person-link.component';
import { EventLinkComponent } from '../../components/shared/event-link.component';
import { UsersService } from '../../services/users-service.service';
import {
  MEMBER_TIERS,
  MemberRowDto,
  MemberTier,
  MembersBoardDto,
} from '../../data-transfer-objects/members.dto';

type SortKey =
  | 'score'
  | 'activeDays30'
  | 'chatMessages90'
  | 'eventsJoined90'
  | 'lastSeen'
  | 'contactedAt';

/** Which members to show by how the team's outreach has gone with them. */
type OutreachFilter = 'any' | 'not-contacted' | 'contacted' | 'replied' | 'no-reply';

/**
 * The Members board: who actually uses NX2U.
 *
 * One row per account, one score, one tier. The engagement board answers
 * "did this conversation go anywhere"; the users list answers "who is this
 * person". Neither answers "who are my hundred most engaged members", which
 * is the question every outreach starts with, and the one this board is for.
 *
 * The rows come from a nightly snapshot rather than a live query: presence is
 * ninety days of minute-level pings and chat counts live in Firestore, so a
 * live join would take seconds per load and would still be wrong about chat.
 * The Recompute button is for the day the snapshot is not fresh enough.
 *
 * Internal and blocked accounts are on the list but off by default, and never
 * in the totals: a persona login and a blocked scammer are the two accounts
 * most likely to top a naive activity ranking. Each toggle carries the number
 * it is holding back, so an empty one reads as "none of those" rather than as
 * a filter nobody noticed.
 *
 * Whether an account is blocked is read live when the board is served, not
 * taken from the nightly snapshot: a block takes effect in seconds and its
 * row has to leave the list at once.
 */
@Component({
  selector: 'app-members',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatProgressSpinnerModule,
    MatMenuModule,
    InfoHintComponent,
    PersonLinkComponent,
    EventLinkComponent,
  ],
  templateUrl: './members.component.html',
  styleUrls: ['./members.component.scss'],
})
export class MembersComponent implements OnInit {
  board: MembersBoardDto | null = null;
  loading = true;
  recomputing = false;
  error: string | null = null;
  notice: string | null = null;

  tiers = MEMBER_TIERS;
  tier: MemberTier | 'all' = 'all';
  showInternal = false;
  showBanned = false;
  search = '';
  outreach: OutreachFilter = 'any';
  sortKey: SortKey = 'score';
  sortDesc = true;

  constructor(private usersService: UsersService) {}

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.loading = true;
    this.error = null;
    this.usersService.getMembersBoard().subscribe({
      next: (board) => {
        this.board = board;
        this.loading = false;
      },
      error: (err) => {
        this.error = `Could not load the members board: ${err?.message ?? err}`;
        this.loading = false;
      },
    });
  }

  /** Rebuild the snapshot now. A few seconds; the board reloads when it is done. */
  recompute(): void {
    if (this.recomputing) return;
    this.recomputing = true;
    this.error = null;
    this.notice = null;
    this.usersService.recomputeMembers().subscribe({
      next: (r) => {
        this.recomputing = false;
        this.notice =
          `Recomputed ${r.members} members from ${r.conversations} conversations ` +
          `(${r.senders} people have ever sent a message).`;
        this.load();
      },
      error: (err) => {
        this.recomputing = false;
        this.error = `Recompute failed: ${err?.error?.error ?? err?.message ?? err}`;
      },
    });
  }

  /** How many rows the internal toggle is hiding right now. */
  get hiddenInternal(): number {
    return this.showInternal ? 0 : this.matching((r) => r.isInternal).length;
  }

  /** How many rows the blocked toggle is hiding right now. */
  get hiddenBlocked(): number {
    return this.showBanned ? 0 : this.matching((r) => r.isBanned && !r.isInternal).length;
  }

  /*
   * Counted after the tier and the search box, so the number answers the
   * question actually being asked - "is anything missing from what I am
   * looking at" - rather than reporting the whole table every time.
   */
  private matching(extra: (r: MemberRowDto) => boolean): MemberRowDto[] {
    const q = this.search.trim().toLowerCase();
    return (this.board?.rows ?? []).filter(
      (r) => (this.tier === 'all' || r.tier === this.tier) && this.searchHit(r, q) && extra(r)
    );
  }

  private searchHit(r: MemberRowDto, q: string): boolean {
    return (
      !q ||
      r.name.toLowerCase().includes(q) ||
      (r.email ?? '').toLowerCase().includes(q) ||
      r.company.toLowerCase().includes(q) ||
      (r.basedIn ?? '').toLowerCase().includes(q)
    );
  }

  get rows(): MemberRowDto[] {
    const rows = this.matching(
      (r) =>
        (this.showInternal || !r.isInternal) &&
        (this.showBanned || !r.isBanned) &&
        this.matchesOutreach(r)
    );
    const dir = this.sortDesc ? -1 : 1;
    return rows.sort((a, b) => {
      const av = this.sortValue(a);
      const bv = this.sortValue(b);
      if (av === bv) return b.score - a.score;
      return (av > bv ? 1 : -1) * dir;
    });
  }

  /**
   * The value a row sorts on.
   *
   * contactedAt is null for everyone nobody has written to, and comparing a
   * string with null is false in both directions - which would leave every
   * uncontacted member "equal" to every date and scatter them through the
   * column. An empty string sorts below every real timestamp instead.
   */
  private sortValue(r: MemberRowDto): string | number {
    const value = r[this.sortKey];
    return value ?? '';
  }

  /**
   * The outreach filter. "Contacted, no reply" deliberately means contacted
   * and silent, not simply silent: a member nobody has written to has not
   * ignored anyone.
   */
  private matchesOutreach(r: MemberRowDto): boolean {
    switch (this.outreach) {
      case 'not-contacted':
        return !r.contactedAt;
      case 'contacted':
        return !!r.contactedAt;
      case 'replied':
        return r.repliedToTeam;
      case 'no-reply':
        return !!r.contactedAt && !r.repliedToTeam;
      default:
        return true;
    }
  }

  sortBy(key: SortKey): void {
    if (this.sortKey === key) {
      this.sortDesc = !this.sortDesc;
      return;
    }
    this.sortKey = key;
    this.sortDesc = true;
  }

  tierCount(tier: MemberTier): number {
    return this.board?.totals.byTier[tier] ?? 0;
  }

  tierLabel(tier: MemberTier): string {
    return {
      core: 'Core',
      returning: 'Returning',
      new: 'New',
      occasional: 'Occasional',
      lapsed: 'Lapsed',
      dormant: 'Dormant',
    }[tier];
  }

  /**
   * The spaces popup is one menu shared by every row rather than one menu per
   * row: the board renders thousands of rows, and a menu each would be
   * thousands of components standing by to show a list of at most 49 lines.
   * The row's spaces are handed over as trigger data when it opens.
   */
  eventMenuData(r: MemberRowDto): { events: MemberRowDto['events']; name: string } {
    return { events: r.events, name: r.name || r.uid };
  }

  /** "Role · Company · City", skipping whatever is blank. */
  byline(r: MemberRowDto): string {
    return [r.role, r.company, r.basedIn].filter((s) => s && s.trim()).join(' · ');
  }
}
